import { makeStyles } from 'tss-react/mui';
import { OrderBar, IconButtonLink } from '../../components';
import { ArrowBackIcon, CallMergeIcon } from '../../assets';
import { generateUrl } from '../../lib';
import { useSelector, orderSelectors } from '../../state';
import { MergedOrderGroupProgress } from '../../types/orderTypes';

interface Props {
  children?: React.ReactNode;
  header?: string;
  hideHeader?: boolean;
  smallHeader?: string;
  backNavigationPath?: string;
  orderBar?: React.ReactNode;
  orderNumber?: string | number;
  style?: React.CSSProperties;
}

export function ProcessContainer({
  children,
  header,
  hideHeader = false,
  smallHeader,
  backNavigationPath,
  orderBar,
  orderNumber,
  style,
}: Props) {
  const { classes, cx } = useStyles();
  const selectedOrder = useSelector(orderSelectors.selectedOrder);
  const mergeProgress: MergedOrderGroupProgress | null | undefined =
    selectedOrder?.mergedOrderGroupProgress;

  return (
    <div className={classes.wrapper}>
      {!!mergeProgress && (
        <div className={classes.mergeBanner}>
          <CallMergeIcon style={{ fontSize: 18, marginRight: 8 }} />
          <span>
            You are processing a merge group &middot;{' '}
            {mergeProgress.allPacked
              ? `All ${mergeProgress.totalCount} orders packed`
              : `Order ${Math.min(mergeProgress.packedCount + 1, mergeProgress.totalCount)} of ${mergeProgress.totalCount}`}
          </span>
        </div>
      )}
      <div className={classes.container}>
        {orderBar && <OrderBar orderNumber={orderNumber} />}
        <div
          className={cx(classes.content, {
            [classes.contentNoHeader]: !header,
          })}
          style={style}
        >
          {smallHeader}
          {!hideHeader && (
            <div className={classes.headerContainer}>
              {!!backNavigationPath && (
                <IconButtonLink
                  to={generateUrl(backNavigationPath)}
                  className={classes.backIcon}
                  size='small'
                >
                  <ArrowBackIcon />
                </IconButtonLink>
              )}
              {!!header && <h1 className={classes.header}>{header}</h1>}
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles({ name: { ProcessContainer } })((theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100vh',
    [theme.breakpoints.down('tablet')]: {
      height: '100%',
    },
  },
  container: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    [theme.breakpoints.down('tablet')]: {
      height: '100%',
    },
  },
  mergeBanner: {
    backgroundColor: '#6C63FF',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '8px 16px',
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 600,
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    // justifyContent: "center",
    width: '100%',
    minHeight: 0,
    overflow: 'auto',
    padding: '32px 16px 40px 16px',
    [theme.breakpoints.down('tablet')]: {
      padding: '24px 16px',
    },
  },
  contentNoHeader: {
    padding: '0px 16px 40px 16px',
  },
  header: {
    fontFamily: 'Montserrat',
    fontSize: 32,
    fontWeight: 500,
    letterSpacing: 0,
    [theme.breakpoints.down('tablet')]: {
      fontSize: 26,
    },
  },
  headerContainer: {
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
  },
  backIcon: {
    color: 'inherit',
    marginRight: 7,
  },
}));
